import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import CtaChips from "../components/composer/CtaChips";
import GridButton from "../components/composer/CatalogButton";
import VoiceInputButton from "../components/composer/VoiceInputButton";
import { FiShoppingCart } from "react-icons/fi";
import SendBoldIcon from "../icons/SendBoldIcon";
import { trackEvent } from "../services/amplitude";
import "../styles/Composer.css";

const TA_MAX_H = 120;

export default function Composer({
  ctas,
  loading,
  onSend,
  onCtaClick,
  cartCount = 0,
  onCartClick,
  onGridClick,
  cartOverlay = false,
  chatId = "",
}) {
  const { t, i18n } = useTranslation("composer");

  const [input, setInput] = useState("");
  const [isVoiceListening, setIsVoiceListening] = useState(false);

  const canSend = useMemo(() => input.trim().length > 0, [input]);
  const showSendButton = canSend && !isVoiceListening;
  const isCartFull = cartCount > 0;

  const taRef = useRef(null);
  const rootRef = useRef(null);

  const resizeTextarea = () => {
    const el = taRef.current;
    if (!el) return;

    el.style.height = "0px";
    const next = Math.min(el.scrollHeight, TA_MAX_H);
    el.style.height = `${next}px`;
    el.style.overflowY = el.scrollHeight > TA_MAX_H ? "auto" : "hidden";
  };

  useEffect(() => {
    resizeTextarea();
  }, [input]);

  useEffect(() => {
    resizeTextarea();
  }, []);

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;

    const set = () => {
      const h = el.offsetHeight || 0;
      document.documentElement.style.setProperty("--composer-h", `${h}px`);
    };

    set();

    const ro = new ResizeObserver(set);
    ro.observe(el);

    return () => ro.disconnect();
  }, []);

  function onSubmit(e) {
    e.preventDefault();
    if (!canSend) return;

    const text = input.trim();

    trackEvent("message_sent", {
  chat_id: chatId || null,
  message_length: text.length,
  message_preview: text.slice(0, 50), // перші 50 символів
  lang: i18n.language,
  source: "composer",
});

    setInput("");
    onSend(text);

    requestAnimationFrame(() => {
      resizeTextarea();
    });
  }

  if (cartOverlay) {
    return (
      <div className="cartOverlayBar">
        <button
          type="button"
          className={`headerCartBtn cartBottomBtn ${isCartFull ? "isFull" : ""}`}
          onClick={onCartClick}
          aria-label={t("cart")}
          title={t("cart")}
          disabled={loading}
        >
          <FiShoppingCart className="cartIconReact" />
          {isCartFull && (
            <span className="cartBadge">{cartCount > 99 ? "99+" : cartCount}</span>
          )}
        </button>
      </div>
    );
  }

  return (
    <footer ref={rootRef} className="composer">
      <div className="composerInner">
        <div className="ctaRow">
          <GridButton onClick={onGridClick} disabled={loading} />
          <CtaChips ctas={ctas} loading={loading} onClick={onCtaClick} />
        </div>

        <form onSubmit={onSubmit} className="inputRow">
          <button
            type="button"
            className={`headerCartBtn cartBottomBtn ${isCartFull ? "isFull" : ""}`}
            onClick={onCartClick}
            aria-label={t("cart")}
            title={t("cart")}
            disabled={loading}
          >
            <FiShoppingCart className="cartIconReact" />
            {isCartFull && (
              <span className="cartBadge">{cartCount > 99 ? "99+" : cartCount}</span>
            )}
          </button>

          <div className={`csInput ${canSend ? "isActive" : ""}`}>
            <div className="csField">
              <textarea
                ref={taRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={t("write_message")}
                disabled={loading}
                rows={1}
                className="csTextarea"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    if (canSend) onSubmit(e);
                  }
                }}
              />
            </div>

            {showSendButton ? (
              <button
                type="submit"
                className="csSendBtn"
                disabled={loading}
                aria-label={t("send")}
                title={t("send")}
              >
                <span className="csSendBtn__icon">
                  <SendBoldIcon />
                </span>
              </button>
            ) : (
              <VoiceInputButton
                loading={loading}
                lang="uk-UA"
                onTranscript={(value) => setInput(value)}
                onListeningChange={setIsVoiceListening}
              />
            )}
          </div>
        </form>
      </div>
    </footer>
  );
}